import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { ENTITY_UNITS } from '@commons/constants/units';
import { INPUT_WIDTH, Tooltip } from '@commons/utils/styledLibraryComponents';
import DisplayNumber, { formatNumber } from '@commons/DisplayNumber';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { getClientInfo } from '@selectors/client';

import {
  computeMargin,
  computeRatio,
  computeTotalCostByChannel,
} from '@admin/products/components/Performances/utils/computeRecipeChannelCost';
import { getProductPriceExcludingVAT } from '@admin/products/products/detail/components/ProductInfo/utils/getProductPriceExcludingVAT.js';

import RecipeDetailActions from '../../../RecipeDetailActions';

import RecipeCompositionSearchBar from '../RecipeCompositionSearchBar';

import {
  FooterContainer,
  FooterLeftContainer,
  FooterRightContainer,
  InformationContainer,
  LabelContainer,
  LinkImage,
} from './styledComponents';

const InformationComponent = ({ label, value, link, tooltipText, width }) => (
  <InformationContainer width={width}>
    <LabelContainer>
      <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
        {label} :
      </Text>
      {!!link && (
        <LinkImage src="/images/inpulse/open-a-new-black-small.svg" onClick={link.onClick} />
      )}
      {!!tooltipText && !!tooltipText.length && (
        <div style={{ marginTop: '-2px' }}>
          <Tooltip displayBigger={true} text={tooltipText} />
        </div>
      )}
    </LabelContainer>
    <Text color={ENUM_COLORS.BLACK} font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
      {value}
    </Text>
  </InformationContainer>
);

export const getEntityUnit = (unit) => {
  if (unit === ENTITY_UNITS.UNIT) {
    return 'u.';
  }

  return unit;
};

const RecipeComponentsFormHeader = ({
  client: { defaultChannelId },
  currency: { numberDecimals },
  allergens,
  addEntity,
  isReadOnly,
  isKitchen,
  recipe,
  recipesAvailable,
  setWarningModalParams,
  supplierProductsAvailable,
  setOpenCreateIngredientModal,
  onSupplierProductsAvailableChange,
  compositions,
  channels,
  currency,
  formFields,
  associatedProduct,
  isDuplicatingRecipe,
  recipeVariants,
  setRecipeVariants,
  setDeletedVariantIds,
  selectedVariantId,
  setSelectedVariantId,
}) => {
  const [totalCost, setTotalCost] = useState({ [defaultChannelId]: null });
  const [compositionForRecipePreparation, setCompositionForRecipePreparation] =
    useState(compositions);

  const computeTotalCost = (entities) =>
    computeTotalCostByChannel(entities, recipe, channels, defaultChannelId);

  const getRatio = (cost, channelId, decimals) =>
    formatNumber(computeRatio(cost, channelId, associatedProduct, decimals), decimals);

  const getMarginRatio = (cost, channelId, decimals) =>
    formatNumber(computeMargin(cost, channelId, associatedProduct, decimals), decimals);

  const openProductDetailsPage = () => {
    isKitchen
      ? window.open(`/admin/kitchen-products/products/${associatedProduct.id}/details`, '_blank')
      : window.open(`/admin/products/${associatedProduct.id}/details`, '_blank');
  };

  useEffect(() => {
    if (!compositions || !compositions.length) {
      // Reset the derived composition so actions relying on its length (create variant, download)
      // disable again once the last component is removed.
      setTotalCost({ [defaultChannelId]: null });
      setCompositionForRecipePreparation([]);
      return;
    }

    if (!formFields || !formFields.length) {
      return;
    }

    const totalCompositionCost = computeTotalCost(formFields);

    setTotalCost(totalCompositionCost);

    setCompositionForRecipePreparation(compositions);
  }, [compositions, recipe.hasMultipleChannels]);

  const getRatioToolTipAndValue = () => {
    if (!recipe.hasMultipleChannels) {
      return {
        tooltip: i18next.t('ADMIN.RECIPES.PRICE_COST_RATIO'),
        value: `${getRatio(
          totalCost[defaultChannelId],
          defaultChannelId,
          currency.numberDecimals,
        )}%`,
      };
    }

    const tooltipChannelNames = channels
      .map((channel) =>
        i18next.t('ADMIN.RECIPES.PRICE_COST_RATIO_MULTIPLE_CHANNELS_NAMES_TOOLTIP', {
          channelName: channel.fullName,
        }),
      )
      .join(' | ');

    const tooltipCalculations = i18next.t(
      'ADMIN.RECIPES.PRICE_COST_RATIO_MULTIPLE_CHANNELS_CALCULATIONS_TOOLTIP',
    );

    return {
      tooltip: `${tooltipChannelNames} - ${tooltipCalculations}`,
      value:
        Object.entries(totalCost)
          .map(([channelId, cost]) => {
            const ratio = getRatio(cost, channelId, currency.numberDecimals);
            return ratio || '- ';
          })
          .join('% | ') + '%',
    };
  };

  const getMarginRatioToolTipAndValue = () => {
    if (!recipe.hasMultipleChannels) {
      return {
        tooltip: i18next.t('ADMIN.PRODUCTS.COMPOSITION_MARGIN_RATIO'),
        value: `${getMarginRatio(
          totalCost[defaultChannelId],
          defaultChannelId,
          currency.numberDecimals,
        )}%`,
      };
    }

    const tooltipChannelNames = channels
      .map((channel) =>
        i18next.t('ADMIN.PRODUCTS.COMPOSITION_MARGIN_RATIO_MULTI_CHANNEL_NAMES_TOOLTIP', {
          channelName: channel.fullName,
        }),
      )
      .join(' | ');

    const tooltipCalculations = i18next.t(
      'ADMIN.PRODUCTS.COMPOSITION_MARGIN_RATIO_MULTI_CHANNEL_CALCULATIONS_TOOLTIP',
    );

    return {
      tooltip: `${tooltipChannelNames} - ${tooltipCalculations}`,
      value:
        Object.entries(totalCost)
          .map(([channelId, cost]) => {
            const marginRatio = getMarginRatio(cost, channelId, currency.numberDecimals);
            return marginRatio || '- ';
          })
          .join('% | ') + '%',
    };
  };

  const { tooltip: ratioTooltip, value: ratioValue } = getRatioToolTipAndValue();
  const { tooltip: marginRatioTooltip, value: marginRatioValue } = getMarginRatioToolTipAndValue();

  return (
    <FooterContainer>
      <FooterLeftContainer>
        <RecipeCompositionSearchBar
          addEntity={addEntity}
          channels={channels}
          compositions={compositions}
          iconSrc={'/images/inpulse/search-dmgrey-small.svg'}
          isReadOnly={isReadOnly}
          placeholder={i18next.t('ADMIN.RECIPES.ADD_COMPONENT')}
          recipe={recipe}
          recipesAvailable={recipesAvailable}
          setWarningModalParams={setWarningModalParams}
          supplierProductsAvailable={supplierProductsAvailable}
          width={INPUT_WIDTH.LARGE}
          onCreationClick={() => setOpenCreateIngredientModal(true)}
          onSupplierProductsAvailableChange={onSupplierProductsAvailableChange}
        />
        {!isEmpty(associatedProduct) && !isDuplicatingRecipe && (
          <InformationComponent
            label={i18next.t('ADMIN.PRODUCTS.INFO_TITLE')}
            link={{
              onClick: openProductDetailsPage,
            }}
            value={associatedProduct.name}
            width={'240px'}
          />
        )}
        <InformationComponent
          label={i18next.t('ADMIN.RECIPES.COST_EX_TAX_WITH_CURRENCY_BY_UNIT', {
            currency: currency.alphabeticCode,
          })}
          tooltipText={
            recipe.hasMultipleChannels
              ? channels
                  .map((channel) =>
                    i18next.t('ADMIN.RECIPES.DETAILS_COSTS_TOOLTIP_MULTIPLE_CHANNELS', {
                      channelName: channel.fullName,
                    }),
                  )
                  .join(' | ')
              : null
          }
          value={
            recipe.hasMultipleChannels ? (
              // "|| null" so we show '-' when no quantity is selected
              Object.values(totalCost)
                .map((cost) => formatNumber(cost || null, currency.numberDecimals))
                .join(' | ')
            ) : (
              <DisplayNumber currency={currency} number={totalCost[defaultChannelId]} />
            )
          }
        />
        {!isEmpty(associatedProduct) && !isDuplicatingRecipe && (
          <>
            <InformationComponent
              label={i18next.t('ADMIN.RECIPES.PRICE_EXCL_TAXES_WITH_CURRENCY', {
                alphabeticCode: currency.alphabeticCode,
              })}
              tooltipText={
                recipe.hasMultipleChannels &&
                channels
                  .map((channel) =>
                    i18next.t('ADMIN.RECIPES.PRICE_EXCL_TAXES_MULTIPLE_CHANNELS_TOOLTIP', {
                      channelName: channel.fullName,
                    }),
                  )
                  .join(' | ')
              }
              value={
                recipe.hasMultipleChannels
                  ? channels
                      .map(({ id }) =>
                        formatNumber(
                          getProductPriceExcludingVAT(associatedProduct, id),
                          currency.numberDecimals,
                        ),
                      )
                      .join(' | ')
                  : formatNumber(
                      getProductPriceExcludingVAT(associatedProduct, defaultChannelId),
                      currency.numberDecimals,
                    )
              }
            />
            <InformationComponent
              label={i18next.t('ADMIN.PRODUCTS.RECIPE_ASSOCIATION_RATIO')}
              tooltipText={ratioTooltip}
              value={ratioValue}
            />
            <InformationComponent
              label={i18next.t('GENERAL.MARGIN')}
              tooltipText={marginRatioTooltip}
              value={marginRatioValue}
            />
          </>
        )}
      </FooterLeftContainer>
      <FooterRightContainer>
        <RecipeDetailActions
          allergens={allergens}
          compositionSettings={compositionForRecipePreparation}
          recipe={recipe}
          recipeVariants={recipeVariants}
          selectedVariantId={selectedVariantId}
          setDeletedVariantIds={setDeletedVariantIds}
          setRecipeVariants={setRecipeVariants}
          setSelectedVariantId={setSelectedVariantId}
        />
      </FooterRightContainer>
    </FooterContainer>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
});

export default connect(mapStateToProps, null)(RecipeComponentsFormHeader);
